import {
  getFieldValue,
  getQuestionnaireText,
  isFieldVisible,
  type AnswerValue,
  type Answers,
  type QuestionnaireDefinition,
  type QuestionnaireField,
} from "@/domain/questionnaire";
import { useI18n } from "@/i18n/useI18n";

interface QuestionnaireFormProps {
  config: QuestionnaireDefinition;
  answers: Answers;
  onChange(key: string, value: AnswerValue): void;
}

/** Editable private questionnaire; conditional fields follow `visibleWhen`. */
export function QuestionnaireForm({ config, answers, onChange }: QuestionnaireFormProps) {
  const { language } = useI18n();
  const text = (value: Parameters<typeof getQuestionnaireText>[0]) =>
    getQuestionnaireText(value, language);

  return (
    <div className="questionnaire-form">
      <h2 className="questionnaire-heading" title={`${config.id} v${config.version}`}>
        {text(config.heading)}
      </h2>
      <p className="questionnaire-introduction">{text(config.introduction)}</p>
      {config.questions.map((question, index) => {
        if (question.type === "separator") {
          return (
            <h3 key={`separator-${index}`} className="questionnaire-separator">
              {text(question.title)}
            </h3>
          );
        }
        const headingId = `question-${question.number}`;
        return (
          <section
            key={question.number ?? index}
            className="questionnaire-question"
            aria-labelledby={headingId}
          >
            <h3 className="questionnaire-question-title" id={headingId}>
              {question.number} {text(question.title)}
            </h3>
            {question.helpText && <p className="questionnaire-help">{text(question.helpText)}</p>}
            {(question.fields ?? [])
              .filter((field) => isFieldVisible(field, answers))
              .map((field) => (
                <QuestionnaireFieldControl
                  key={field.key}
                  field={field}
                  value={getFieldValue(answers, field)}
                  onChange={onChange}
                />
              ))}
          </section>
        );
      })}
    </div>
  );
}

interface FieldProps {
  field: QuestionnaireField;
  value: AnswerValue;
  onChange(key: string, value: AnswerValue): void;
}

function QuestionnaireFieldControl({ field, value, onChange }: FieldProps) {
  const { language } = useI18n();
  const label = getQuestionnaireText(field.label, language);
  const controlId = `questionnaire-${field.id}`;

  if (field.type === "radio" || field.type === "checkbox") {
    const selected = Array.isArray(value) ? value.map(String) : [String(value ?? "")];
    const toggle = (optionValue: string, checked: boolean) => {
      if (field.type === "radio") onChange(field.key, optionValue);
      else
        onChange(
          field.key,
          checked ? [...selected, optionValue] : selected.filter((entry) => entry !== optionValue),
        );
    };
    return (
      <div className="questionnaire-field">
        <label>{label}</label>
        <div
          className="questionnaire-options"
          role={field.type === "radio" ? "radiogroup" : "group"}
          aria-label={label}
        >
          {(field.options ?? []).map((option) => (
            <label key={option.value} className="questionnaire-option">
              <input
                type={field.type}
                name={controlId}
                value={option.value}
                checked={selected.includes(option.value)}
                onChange={(event) => toggle(option.value, event.target.checked)}
              />
              <span>{getQuestionnaireText(option.label, language)}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  const stringValue = value == null ? "" : String(value);
  return (
    <div className="questionnaire-field">
      <label htmlFor={controlId}>{label}</label>
      {field.type === "textarea" ? (
        <textarea
          id={controlId}
          value={stringValue}
          onChange={(event) => onChange(field.key, event.target.value)}
        />
      ) : (
        <input
          id={controlId}
          type={field.type === "number" ? "number" : "text"}
          min={field.type === "number" ? 0 : undefined}
          step={field.type === "number" ? 1 : undefined}
          value={stringValue}
          onChange={(event) => onChange(field.key, event.target.value)}
        />
      )}
    </div>
  );
}
